import { Injectable, inject } from '@angular/core';
import { PinValidator } from '@merchant-portal/app/core/guard/pin.guard.validator';
import { PinDialogMode, PinDialogStep } from '@merchant-portal/app/models/dialog/dialog-type';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { EMPTY, filter, switchMap, tap, withLatestFrom } from 'rxjs';
import { PinApiService } from '../../api/pin-api.service';

export interface PinDialogState {
  currentPin?: string;
  newPin?: string;
  confirmPin?: string;
  isLoading: boolean;
  errorMessage?: string;
  isPinValid: boolean;
  mode: PinDialogMode;
  currentStep: PinDialogStep;
  isWithoutValidation: boolean;
  isSkipDirectCreation: boolean;
}

@Injectable()
export class PinDialogStore extends ComponentStore<PinDialogState> {
  private readonly pinApi = inject(PinApiService);

  constructor() {
    super({
      isLoading: false,
      isPinValid: false,
      mode: PinDialogMode.ValidateExisting,
      currentStep: PinDialogStep.EnterCurrent,
      isWithoutValidation: false,
      isSkipDirectCreation: false,
    });
  }

  override readonly state$ = this.select(state => state);
  readonly isLoading$ = this.select(state => state.isLoading);
  readonly errorMessage$ = this.select(state => state.errorMessage);
  readonly isPinValid$ = this.select(state => state.isPinValid);
  readonly currentStep$ = this.select(state => state.currentStep);
  readonly mode$ = this.select(state => state.mode);

  readonly setMode = this.updater<PinDialogMode>((state, mode) => {
    const initialStep = this.getInitialStep(mode);
    return {
      ...state,
      mode,
      currentStep: initialStep,
      currentPin: undefined,
      newPin: undefined,
      confirmPin: undefined,
      errorMessage: undefined,
      isPinValid: false,
    };
  });

  readonly setWithoutValidation = this.updater<boolean>((state, isWithoutValidation) => ({
    ...state,
    isWithoutValidation,
  }));

  readonly setSkipDirectCreation = this.updater<boolean>((state, isSkipDirectCreation) => ({
    ...state,
    isSkipDirectCreation,
  }));

  readonly setCurrentPin = this.updater<string>((state, currentPin) => ({
    ...state,
    currentPin,
    errorMessage: undefined,
  }));

  readonly setNewPin = this.updater<string>((state, newPin) => ({
    ...state,
    newPin,
    errorMessage: undefined,
  }));

  readonly setConfirmPin = this.updater<string>((state, confirmPin) => ({
    ...state,
    confirmPin,
    errorMessage: undefined,
  }));

  readonly validateCurrentPin = this.effect<void>(trigger$ =>
    trigger$.pipe(
      tap(() => this.patchState({ isLoading: true, errorMessage: undefined })),
      withLatestFrom(
        this.select(s => ({ pin: s.currentPin, isWithoutValidation: s.isWithoutValidation }))
      ),
      switchMap(([, { pin, isWithoutValidation }]) => {
        if (isWithoutValidation) {
          this.patchState({
            isPinValid: true,
            isLoading: false,
            currentStep: this.getNextStep(PinDialogStep.EnterCurrent, this.get().mode),
          });
          return EMPTY;
        }

        if (!pin) {
          this.patchState({ isLoading: false, errorMessage: 'PIN is required' });
          return EMPTY;
        }

        return this.pinApi.checkPin(pin).pipe(
          tapResponse(
            () => {
              this.patchState({
                isPinValid: true,
                isLoading: false,
                currentStep: this.getNextStep(PinDialogStep.EnterCurrent, this.get().mode),
              });
            },
            (error: any) => {
              this.patchState({
                isLoading: false,
                errorMessage: error?.error?.errors || error.message || 'Invalid PIN',
                isPinValid: false,
              });
            }
          )
        );
      })
    )
  );

  readonly validateNewPin = this.effect<void>(trigger$ =>
    trigger$.pipe(
      withLatestFrom(this.select(s => ({ newPin: s.newPin, confirmPin: s.confirmPin }))),
      tap(([, { newPin, confirmPin }]) => {
        if (!newPin) {
          this.patchState({ errorMessage: 'New PIN is required' });
          return;
        }

        if (newPin.length !== 6) {
          this.patchState({ errorMessage: 'PIN must be 6 digits' });
          return;
        }

        if (confirmPin && newPin !== confirmPin) {
          this.patchState({ errorMessage: 'PIN doesn’t match the new PIN' });
          return;
        }

        const err = PinValidator.isValidPin(newPin);
        if (err) {
          this.patchState({ isLoading: false, errorMessage: err });
          return;
        }

        this.patchState({
          errorMessage: undefined,
          currentStep: this.getNextStep(PinDialogStep.EnterNew, this.get().mode),
        });
      })
    )
  );

  readonly confirmNewPin = this.effect<void>(trigger$ =>
    trigger$.pipe(
      withLatestFrom(this.select(s => ({ newPin: s.newPin, confirmPin: s.confirmPin }))),
      tap(([, { newPin, confirmPin }]) => {
        if (!confirmPin) {
          this.patchState({ errorMessage: 'Please confirm your PIN' });
          return;
        }

        if (newPin !== confirmPin) {
          this.patchState({ errorMessage: 'PIN doesn’t match the new PIN', isLoading: false });
          return;
        }

        this.patchState({
          errorMessage: undefined,
          currentStep: PinDialogStep.Done,
          isPinValid: true,
        });
      })
    )
  );

  readonly createPin = this.effect<void>(trigger$ =>
    trigger$.pipe(
      tap(() => this.patchState({ isLoading: true })),
      withLatestFrom(
        this.select(s => ({
          newPin: s.newPin,
          isSkipDirectCreation: s.isSkipDirectCreation,
          errorMessage: s.errorMessage,
        }))
      ),
      tap(([, { isSkipDirectCreation, errorMessage }]) => {
        // when its skip direct creation and have error
        // then set the loading state to be false
        // to enable user re-validate pin again
        if (isSkipDirectCreation && errorMessage) {
          this.patchState({ isLoading: false });
        }
      }),
      filter(([, { newPin, errorMessage }]) => !!newPin && !errorMessage),
      switchMap(([, { newPin, isSkipDirectCreation }]) => {
        if (isSkipDirectCreation) {
          this.patchState({
            isLoading: false,
            currentStep: PinDialogStep.Done,
            isPinValid: true,
          });
          return EMPTY;
        }

        return this.pinApi.createPin(newPin!).pipe(
          tapResponse(
            () => {
              this.patchState({
                isLoading: false,
                currentStep: PinDialogStep.Done,
                isPinValid: true,
              });
            },
            (error: any) => {
              this.patchState({
                isLoading: false,
                errorMessage: error?.error?.errors || error.message || 'Failed to create PIN',
              });
            }
          )
        );
      })
    )
  );

  readonly changePin = this.effect<void>(trigger$ =>
    trigger$.pipe(
      tap(() => this.patchState({ isLoading: true })),
      withLatestFrom(this.select(s => ({ currentPin: s.currentPin, newPin: s.newPin }))),
      filter(([, { currentPin, newPin }]) => !!currentPin && !!newPin),
      switchMap(([, { currentPin, newPin }]) =>
        this.pinApi.changePin({ pin: currentPin!, newPin: newPin! }).pipe(
          tapResponse(
            () => {
              this.patchState({
                isLoading: false,
                currentStep: PinDialogStep.Done,
                isPinValid: true,
              });
            },
            (error: any) => {
              this.patchState({
                isLoading: false,
                errorMessage: error?.error?.errors || error.message || 'Failed to change PIN',
              });
            }
          )
        )
      )
    )
  );

  private getInitialStep(mode: PinDialogMode): PinDialogStep {
    switch (mode) {
      case PinDialogMode.ValidateExisting:
      case PinDialogMode.OnlyValidate:
        return PinDialogStep.EnterCurrent;
      case PinDialogMode.CreateNew:
        return PinDialogStep.EnterNew;
      case PinDialogMode.ChangePin:
        return PinDialogStep.EnterCurrent;
      default:
        return PinDialogStep.EnterCurrent;
    }
  }

  private getNextStep(currentStep: PinDialogStep, mode: PinDialogMode): PinDialogStep {
    switch (mode) {
      case PinDialogMode.ValidateExisting:
      case PinDialogMode.OnlyValidate:
        return currentStep === PinDialogStep.EnterCurrent ? PinDialogStep.Done : currentStep;
      case PinDialogMode.CreateNew:
        if (currentStep === PinDialogStep.EnterNew) return PinDialogStep.ConfirmNew;
        if (currentStep === PinDialogStep.ConfirmNew) return PinDialogStep.Done;
        return currentStep;
      case PinDialogMode.ChangePin:
        if (currentStep === PinDialogStep.EnterCurrent) return PinDialogStep.EnterNew;
        if (currentStep === PinDialogStep.EnterNew) return PinDialogStep.ConfirmNew;
        if (currentStep === PinDialogStep.ConfirmNew) return PinDialogStep.Done;
        return currentStep;
      default:
        return currentStep;
    }
  }
}
